/**
 * Slice 22 — Team invite module permissions.
 * Each granted module = full use of that tab (except `tasks` = do assigned work).
 */

import type { PropertyAccessLevel, PropertyTabId } from "@/lib/propertyTabs";

export const TEAM_PERMISSION_MODULES = [
  "overview",
  "schedule",
  "tasks",
  "maintenance",
  "unit",
  "reports",
  "inventory",
  "access",
  "settings",
] as const;

export type TeamPermissionModule = (typeof TEAM_PERMISSION_MODULES)[number];

/** Legacy rows (NULL permissions) get tabs but not Tasks execute. */
export const LEGACY_TEAM_PERMISSION_MODULES: TeamPermissionModule[] = [
  "overview",
  "schedule",
  "maintenance",
  "unit",
  "reports",
  "inventory",
  "access",
  "settings",
];

export const TEAM_PERMISSION_LABELS: Record<TeamPermissionModule, string> = {
  overview: "Overview",
  schedule: "Scheduling",
  tasks: "Tasks",
  maintenance: "Issues",
  unit: "Unit",
  reports: "Reports",
  inventory: "Inventory",
  access: "Access",
  settings: "Settings",
};

export const TEAM_PERMISSION_SUBLABELS: Partial<Record<TeamPermissionModule, string>> = {
  schedule: "assign tasks",
  tasks: "do assigned work",
  access: "keys & entry",
};

const TAB_TO_MODULE: Partial<Record<PropertyTabId, TeamPermissionModule>> = {
  overview: "overview",
  schedule: "schedule",
  "my-tasks": "tasks",
  maintenance: "maintenance",
  unit: "unit",
  reports: "reports",
  inventory: "inventory",
  rooms: "inventory",
  access: "access",
  settings: "settings",
};

export function isTeamPermissionModule(value: unknown): value is TeamPermissionModule {
  return (
    typeof value === "string" &&
    (TEAM_PERMISSION_MODULES as readonly string[]).includes(value)
  );
}

/** Normalize DB jsonb / unknown into a deduped module list. */
export function parseTeamPermissions(raw: unknown): TeamPermissionModule[] | null {
  if (raw == null) return null;
  if (!Array.isArray(raw)) return null;
  const out: TeamPermissionModule[] = [];
  for (const item of raw) {
    if (isTeamPermissionModule(item) && !out.includes(item)) out.push(item);
  }
  return out;
}

/**
 * Effective permissions for a cohost.
 * Null/missing DB value → legacy tab set (no Tasks).
 */
export function resolveTeamPermissions(
  raw: unknown,
  accessLevel?: PropertyAccessLevel | null
): TeamPermissionModule[] {
  const parsed = parseTeamPermissions(raw);
  if (parsed && parsed.length > 0) return parsed;
  if (parsed && parsed.length === 0) return [];
  void accessLevel;
  return [...LEGACY_TEAM_PERMISSION_MODULES];
}

export function hasTeamPermission(
  permissions: TeamPermissionModule[] | null | undefined,
  module: TeamPermissionModule
): boolean {
  if (!permissions) return module !== "tasks";
  return permissions.includes(module);
}

export function tabAllowedByPermissions(
  tabId: PropertyTabId,
  permissions: TeamPermissionModule[] | null | undefined
): boolean {
  const module = TAB_TO_MODULE[tabId];
  if (!module) return false;
  return hasTeamPermission(permissions, module);
}

/** Derive legacy access_level: Full Access only when every module is granted. */
export function accessLevelFromPermissions(
  permissions: TeamPermissionModule[]
): PropertyAccessLevel {
  return isAllPermissionsSelected(permissions) ? "full" : "limited";
}

/** Quiet team-row subtitle: `Team · Scheduling · Tasks` or `Team · Full Access`. */
export function formatTeamPermissionsSubtitle(
  permissions: unknown,
  opts?: { scopeLabel?: string; pendingSuffix?: string }
): string {
  const modules = resolveTeamPermissions(permissions);
  const mid = isAllPermissionsSelected(modules)
    ? "Full Access"
    : modules.map((m) => TEAM_PERMISSION_LABELS[m]).join(" · ");
  const parts = ["Team"];
  if (opts?.scopeLabel) parts.push(opts.scopeLabel);
  if (mid) parts.push(mid);
  if (opts?.pendingSuffix) parts.push(opts.pendingSuffix);
  return parts.join(" · ");
}

export function toggleTeamPermission(
  current: TeamPermissionModule[],
  module: TeamPermissionModule | "all"
): TeamPermissionModule[] {
  if (module === "all") {
    const allOn =
      current.length === TEAM_PERMISSION_MODULES.length &&
      TEAM_PERMISSION_MODULES.every((m) => current.includes(m));
    return allOn ? [] : [...TEAM_PERMISSION_MODULES];
  }
  if (current.includes(module)) {
    return current.filter((m) => m !== module);
  }
  return [...current, module];
}

export function isAllPermissionsSelected(current: TeamPermissionModule[]): boolean {
  return (
    current.length === TEAM_PERMISSION_MODULES.length &&
    TEAM_PERMISSION_MODULES.every((m) => current.includes(m))
  );
}
